import React from 'react';
import PropTypes from 'prop-types';

import { LISTING_STATUS_ACTIVE, LISTING_DATA_SOURCE_SUNROOM } from 'common/listingConstants';

import {
  isListingTenantScreening,
  isSelfShowingListing,
  getOwnerPaysForFields,
} from 'common/listingHelpers';

import { listingFormFields } from 'common/listingFormFields';

import { getApplicationLimit } from 'common/householdApplication/applicationHelpers';

const SrAiChatboxListingContext = ({ listing, householdsApplyingToListing }) => {
  const listingActive = listing.status === LISTING_STATUS_ACTIVE;
  const isSelfShowing = isSelfShowingListing(listing);
  const ownerPaysForFields = getOwnerPaysForFields(listing);
  const scheduleWithPendingLandlord =
    !isSelfShowing && householdsApplyingToListing?.delivered?.excludingCurrentHousehold > 0;

  const householdsPaidAppButtonBlocked =
    listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
    !isListingTenantScreening(listing) &&
    householdsApplyingToListing &&
    householdsApplyingToListing.tentativeQualifiedAndProcessing.excludingCurrentHousehold >=
      getApplicationLimit(listing); // eslint-disable-line max-len

  const householdsAcceptedAppButtonBlocked =
    listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
    !isListingTenantScreening(listing) &&
    householdsApplyingToListing &&
    householdsApplyingToListing.accepted.excludingCurrentHousehold >= 1;

  const tourStatus = listing.cantTourFlag
    ? 'The property is not available for tours at this moment, you can still ' +
      'apply and we will notify you when the property is ready to be toured.'
    : !listingActive
    ? 'Home is not open for tours. This property is not currently available for leasing.'
    : isSelfShowing
    ? 'Open for self-touring.'
    : scheduleWithPendingLandlord
    ? 'This property is not available for tours at this moment. A qualified ' +
      'application have already been submitted to the landlord. At this point ' +
      'no tours can be scheduled. If the landlord denies their application, ' +
      'then tours can be scheduled at that point.'
    : 'Renter must schedule a tour and meet a Sunroom showing agent at the ' +
      'property in-person. They will walk you through and help answer your ' +
      'questions! To schedule the tour please click "See This Home" and ' +
      "pick a date and time to meet at the property. It's totally free to tour.";

  const applicationStatus = !listingActive
    ? 'Home is not accepting applications. This property is not currently available for leasing.'
    : householdsPaidAppButtonBlocked
    ? 'Home is not accepting applications. 3 qualified applications have already been ' +
      'submitted. Many tentatively qualified applications have been submitted but have ' +
      'not finished processing, or pending landlord’s decision. At this point no new ' +
      'applications can be submitted.'
    : householdsAcceptedAppButtonBlocked
    ? "1 renter has an approved app but has not signed the lease yet. It's possible " +
      "they won't move forward, so you can still apply, but it's likely they will " +
      'pay the deposit and sign the lease.'
    : 'Home is accepting applications. There are no qualified applications submitted, ' +
      'feel free to apply now!';

  const [utiliesLandlord, utiliesRenter] = Object.keys(ownerPaysForFields).reduce(
    (memo, fieldId) => {
      const fieldTitle = listingFormFields
        .find(({ id }) => id === fieldId)
        .title.replace('Owner Pays ', '');
      memo[ownerPaysForFields[fieldId] ? 0 : 1].push(fieldTitle);
      return memo;
    },
    [[], []],
  );

  return (
    <div className="aichatbot hidden">
      <div>
        <div>Statuses.</div>
        <div>Tour Status: {tourStatus}</div>
        <div>Application Status: {applicationStatus}</div>
      </div>
      <div>
        <div>Utility information.</div>
        <div>
          Utilities the renter is responsible to pay for in addition to rent:{' '}
          {utiliesRenter.join(', ')}
        </div>
        <div>
          {utiliesLandlord.length === 0
            ? 'Utilities are not included in the rent price.'
            : `Utilities included in the rent: ${utiliesLandlord.join(', ')}`}
        </div>
      </div>
    </div>
  );
};

SrAiChatboxListingContext.propTypes = {
  listing: PropTypes.object.isRequired,
  householdsApplyingToListing: PropTypes.object.isRequired,
};

SrAiChatboxListingContext.displayName = 'SrAiChatboxListingContext';

export default SrAiChatboxListingContext;
